import * as Yup from 'yup';
import { StringSchema } from 'yup';
import {
  ContactCategory,
  MASK_TO_PREFIX_MAP,
  PREFIX_TO_MASK_MAP,
} from '../../constants';
import ipUtils, {
  IDENTICAL_IP_ADDRESS_GATEWAY_MESSAGE,
  IPV4_RANGE_INVALID_ERROR_MESSAGE,
  IPV4_RANGE_RESERVED_ERROR_MESSAGE,
  IPv4_REGEX,
  NON_RESERVED_IPV4_ERROR_MESSAGE,
  SINGLE_IPV4_INVALID_ERROR_MESSAGE,
  STATIC_IP_RESERVED_SUBNETS,
} from './ipUtils';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { INVALID_PHONE_NUMBER } from '../../widgets/PhoneNumberInput';
import { SensorCapability } from '../../types';

const YupIpv4Validator = (requiredMessage?: string): StringSchema => {
  let validator = Yup.string().trim();
  if (requiredMessage) {
    validator = validator.required(requiredMessage);
  }
  return validator.test(
    'test ipv4 validity',
    SINGLE_IPV4_INVALID_ERROR_MESSAGE,
    (value) => {
      if (value) {
        return IPv4_REGEX.test(value);
      } else {
        return true;
      }
    },
  );
};

const YupIpv4ValidatorWithReservedRange = (requiredMessage?: string) =>
  YupIpv4Validator(requiredMessage).test(
    'isInRange',
    NON_RESERVED_IPV4_ERROR_MESSAGE,
    (value) => {
      if (!value) return !requiredMessage;
      return !ipUtils.isIPv4InSubnets(value, STATIC_IP_RESERVED_SUBNETS);
    },
  );

const YupIpv4GatewayValidator = (requiredMessage?: string) =>
  YupIpv4Validator(requiredMessage).test(
    'isGatewayValid',
    'Enter a valid gateway IP address',
    function (value) {
      // must not use arrow function in order to access `this` context in Yup
      if (!value) {
        return true;
      }
      let subnetMask: string = this.resolve<string>(Yup.ref('subnetMask'));
      let ipAddress = this.resolve(Yup.ref('ipAddress'));

      if (value === ipAddress) {
        return this.createError({
          message: IDENTICAL_IP_ADDRESS_GATEWAY_MESSAGE,
          path: 'gateway',
        });
      }
      // Must convert to prefix length format
      if (MASK_TO_PREFIX_MAP.hasOwnProperty(subnetMask)) {
        subnetMask =
          MASK_TO_PREFIX_MAP[subnetMask as keyof typeof MASK_TO_PREFIX_MAP];
      }
      const address = this.resolve(Yup.ref('ipAddress')) + '/' + subnetMask;

      // Ensure gateway must be within the static ip address and subnet mask
      return ipUtils.isIPv4InSubnets(value, [address]);
    },
  );

const YupPhoneNumberValidator = (
  name: string,
  requiredMessage?: string,
): StringSchema => {
  let validator = Yup.string();
  if (requiredMessage) {
    validator = validator.required(requiredMessage);
  }
  return validator.test(name, INVALID_PHONE_NUMBER, (value) => {
    if (value) {
      return isPossiblePhoneNumber(value);
    } else {
      return true;
    }
  });
};

const YupIPv4RangeValidator = (fieldName: string): StringSchema => {
  return Yup.string()
    .trim()
    .test('subnet', IPV4_RANGE_INVALID_ERROR_MESSAGE, function (value) {
      let isValid = true;

      if (value) {
        const { ipAddresses, error } = ipUtils.splitIPv4Addresses(value);
        if (error) {
          return this.createError({
            message: error,
            path: fieldName,
          });
        }

        for (const ip of ipAddresses) {
          // Return error if IP is within the reserved subnets
          if (ipUtils.isIPv4InSubnets(ip, STATIC_IP_RESERVED_SUBNETS)) {
            return this.createError({
              message: IPV4_RANGE_RESERVED_ERROR_MESSAGE,
              path: fieldName,
            });
          }
        }
      }
      return isValid;
    });
};

const YupSubnetMaskValidator = Yup.string()
  .trim()
  .required('Enter a subnet mask')
  .test(
    'isSubnetMaskValid',
    'Enter a valid subnet mask or prefix length',
    (value) => {
      if (!value) {
        return false;
      }

      return (
        MASK_TO_PREFIX_MAP.hasOwnProperty(value) ||
        PREFIX_TO_MASK_MAP.hasOwnProperty(value)
      );
    },
  );

const CapabilitiesValidator = (
  schemas?: SensorCapability[],
): Record<string, StringSchema> => {
  if (!schemas) {
    return {};
  }
  const validators: Record<string, StringSchema> = {};
  schemas.forEach((s) => {
    if (s.required) {
      validators[s.capabilityType] = Yup.string().required('Field is required');
    }
  });
  return validators;
};

export const createSiteValidationSchema = (
  siteNames: Set<string>,
  initialSiteName: string | undefined,
) => {
  const siteNameValidator = Yup.string()
    .trim()
    .required('Enter a site name')
    .test('isNameUnique', 'Enter a unique site name', (value) => {
      if (!value) return false;
      return !(siteNames.has(value) && value !== initialSiteName);
    });
  return Yup.object().shape({
    name: siteNameValidator,
    country: Yup.string().trim().required('Make a country selection'),
    street: Yup.string().trim().required('Enter a street address'),
    region: Yup.string()
      .trim()
      .required('Make a state/province/region selection'),
    postcode: Yup.string().trim().required('Enter a postal/ZIP code'),
    city: Yup.string().trim().required('Enter a city name'),
    timezone: Yup.string().trim().required('Make a time zone selection'),
    ipSubnets: YupIPv4RangeValidator('ipSubnets'),
    dns: Yup.array().when('noDNS', {
      is: false,
      then: Yup.array().min(1, 'Make a DNS selection'),
    }),
    dhcp: Yup.array().when('noDHCP', {
      is: false,
      then: Yup.array().min(1, 'Make a DHCP selection'),
    }),
    firewalls: Yup.array().when('noFirewalls', {
      is: false,
      then: Yup.array().min(1, 'Make a firewall selection'),
    }),
    adc: Yup.number()
      .required('Enter the number of domain controllers at this site')
      .min(0, 'Enter a non-negative number of domain controllers at this site'),
  });
};

type SensorScannerValidationSchemaInputs = {
  genericSeriesEnabled?: boolean;
  schemas?: SensorCapability[];
  existingNames?: Set<string>;
  initialName?: string;
};

export const createSensorScannerValidationSchema = ({
  genericSeriesEnabled,
  schemas,
  existingNames,
  initialName,
}: SensorScannerValidationSchemaInputs = {}) =>
  Yup.object().shape({
    name: Yup.string()
      .trim()
      .required('Enter a sensor name')
      .test('isNameUnique', 'Enter a unique sensor name', (value) => {
        if (!value) return false;
        if (!existingNames) return true;
        return !(existingNames.has(value) && value !== initialName);
      }),
    model: genericSeriesEnabled
      ? Yup.string().notRequired()
      : Yup.string().trim().required('Make a sensor model selection'),
    series: genericSeriesEnabled
      ? Yup.string().trim().required('Make a sensor series model selection')
      : Yup.string().notRequired(),
    siteId: Yup.string().trim().required('Make a site selection'),
    currentCapabilities: Yup.object().shape(CapabilitiesValidator(schemas)),
    ipAddress: Yup.string().when('isIPAutoConfig', {
      is: false,
      then: YupIpv4ValidatorWithReservedRange('Enter a static IP address'),
    }),
    subnetMask: Yup.string().when('isIPAutoConfig', {
      is: false,
      then: YupSubnetMaskValidator,
    }),
    gateway: Yup.string().when('isIPAutoConfig', {
      is: false,
      then: YupIpv4GatewayValidator('Enter a gateway IP Address'),
    }),
    primaryDns: Yup.string().when('isIPAutoConfig', {
      is: false,
      then: YupIpv4ValidatorWithReservedRange('Enter a primary DNS server'),
    }),
    secondaryDns: Yup.string().when('isIPAutoConfig', {
      is: false,
      then: YupIpv4ValidatorWithReservedRange(),
    }),
    shippingSiteId: Yup.string().when('isShippingToNewSite', {
      is: false,
      then: Yup.string().trim().required('Make a shipping address selection'),
    }),
    country: Yup.string().when('isShippingToNewSite', {
      is: true,
      then: Yup.string().trim().required('Make a country selection'),
    }),
    street: Yup.string().when('isShippingToNewSite', {
      is: true,
      then: Yup.string().trim().required('Enter a street address'),
    }),
    city: Yup.string().when('isShippingToNewSite', {
      is: true,
      then: Yup.string().trim().required('Enter a city name'),
    }),
    region: Yup.string().when('isShippingToNewSite', {
      is: true,
      then: Yup.string()
        .trim()
        .required('Make a state/province/region selection'),
    }),
    postcode: Yup.string().when('isShippingToNewSite', {
      is: true,
      then: Yup.string().trim().required('Enter a postal/ZIP code'),
    }),
    shippingContactId: Yup.string().when('isShippingToNewContact', {
      is: false,
      then: Yup.string().trim().required('Make a shipping contact selection'),
    }),
    shippingContactFirstName: Yup.string().when('isShippingToNewContact', {
      is: true,
      then: Yup.string().trim().required('Enter a first name'),
    }),
    shippingContactLastName: Yup.string().when('isShippingToNewContact', {
      is: true,
      then: Yup.string().trim().required('Enter a last name'),
    }),
    shippingContactEmail: Yup.string().when('isShippingToNewContact', {
      is: true,
      then: Yup.string()
        .required('Enter an email address')
        .email('Enter a valid email address'),
    }),
    shippingContactPhoneNumber: Yup.string().when('isShippingToNewContact', {
      is: true,
      then: YupPhoneNumberValidator(
        'shippingContactPhoneNumber',
        'Enter a primary phone number',
      ),
    }),
  });

export const ContactValidationSchema = Yup.object().shape({
  firstName: Yup.string().trim().required('Enter a first name'),
  lastName: Yup.string().when(
    'contactCategory',
    (contactCategory: ContactCategory) => {
      return contactCategory !== ContactCategory.DL
        ? Yup.string().trim().required('Enter a last name')
        : Yup.string();
    },
  ),
  email: Yup.string()
    .email('Enter a valid email address')
    .trim()
    .required('Enter an email address'),
  primaryNumber: Yup.string().when(
    'contactCategory',
    (contactCategory: ContactCategory) => {
      return contactCategory !== ContactCategory.DL
        ? YupPhoneNumberValidator(
            'primaryNumber',
            'Enter a primary phone number',
          )
        : YupPhoneNumberValidator('primaryNumber');
    },
  ),
  secondaryNumber: YupPhoneNumberValidator('secondaryNumber'),
});

export const ipConfigFormValidationSchema = Yup.object().shape({
  ipAddress: Yup.string().when('isIPAutoConfig', {
    is: false,
    then: YupIpv4ValidatorWithReservedRange('Enter a static IP address'),
  }),
  subnetMask: Yup.string().when('isIPAutoConfig', {
    is: false,
    then: YupSubnetMaskValidator,
  }),
  gateway: Yup.string().when('isIPAutoConfig', {
    is: false,
    then: YupIpv4GatewayValidator('Enter a gateway IP Address'),
  }),
  primaryDns: Yup.string().when('isIPAutoConfig', {
    is: false,
    then: YupIpv4ValidatorWithReservedRange('Enter a primary DNS server'),
  }),
  secondaryDns: Yup.string().when('isIPAutoConfig', {
    is: false,
    then: YupIpv4ValidatorWithReservedRange(),
  }),
});
